import React from 'react';
import { useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import { Grid, CircularProgress, Button, ButtonGroup } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';

import { useQuery, useMutation } from '@apollo/client';
import i18next from 'i18next';

import SimpleTable from '../../components/table/SimpleTable';
import { FormatDateTime } from '../../components/DateTime';
import FileUploadButton from '../../components/FileUploadButton';
import { dispatchException, dispatchMessage } from '../../helper/snackbar';
import { exportToFile } from '../../helper/download';
import { filterSelector } from '../../helper/filter';
import { userSelector, hasAnyAdminSpace } from '../../helper/security';

import { LIST_TASK_CATEGORIES, LIST_TASK_CATEGORY_GROUPS, LIST_TASK_SUBJECTS } from './gql';

import { ErrorAlert } from '../error';

/**
 * Task subjects list page for managing task subjects.
 * Shows subjects with their category and group hierarchy.
 */
export default function TasksSettingsSubjects() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const filter = filterSelector();
  const user = userSelector()!;

  const { loading, error, data } = useQuery(LIST_TASK_SUBJECTS);

  return (
    <>
      <Helmet>
        <title>{i18next.t('tasks-settings-subjects-title')}</title>
      </Helmet>
      {loading && <CircularProgress />}
      {!loading && error && <ErrorAlert err={error} />}
      {!loading && !error && data && (
        <>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <SimpleTable
                headers={[
                  i18next.t('tasks-field-name'),
                  i18next.t('tasks-field-area'),
                  i18next.t('tasks-field-category'),
                  i18next.t('tasks-field-system-relevant'),
                  i18next.t('tasks-table-status'),
                ]}
                rows={data.listTaskSubjects.map(row => [
                  <Link to={`/tasks/settings/subjects/${row.id}`}>{row.name}</Link>,
                  row.category?.group ? <Link to={`/tasks/settings/groups/${row.category?.group.id}`}>{row.category?.group.name}</Link> : null,
                  row.category ? <Link to={`/tasks/settings/categories/${row.category.id}`}>{row.category.name}</Link> : null,
                  row.system ? <CheckIcon /> : null,
                  row.disabled ? i18next.t('tasks-status-disabled') : i18next.t('tasks-status-active'),
                ])}
              />
            </Grid>
            <Grid item xs={12}>
              {hasAnyAdminSpace(user) && (
                <Button
                  sx={{ marginRight: 2 }}
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/tasks/settings/subjects/_create')}
                >
                  {i18next.t('tasks-settings-add-subject')}
                </Button>
              )}
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
}
